import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Grid,
  Pagination,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleIcon from '@mui/icons-material/People';
import { useAuth } from '../contexts/AuthContext';
import { useEvents } from '../hooks/useEvents';
import type { Event } from '../api/client';

const STATUS_COLORS: Record<string, 'default' | 'success' | 'info' | 'error'> = {
  DRAFT: 'default',
  ACTIVE: 'success',
  COMPLETED: 'info',
  CANCELLED: 'error',
};

function formatDateTime(dateStr: string): string {
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function EventCard({ event }: { event: Event }) {
  const navigate = useNavigate();

  return (
    <Card
      sx={{
        height: '100%',
        transition: 'box-shadow 0.2s, transform 0.2s',
        '&:hover': {
          boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <CardActionArea
        onClick={() => navigate(`/events/${event.id}`)}
        sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
      >
        <CardContent sx={{ flex: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
            <Typography variant="h6" component="h2" noWrap sx={{ flex: 1, mr: 1 }}>
              {event.title}
            </Typography>
            <Chip
              label={event.status}
              color={STATUS_COLORS[event.status] ?? 'default'}
              size="small"
            />
          </Stack>

          <Stack spacing={0.5} mt={2}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <EventIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {formatDateTime(event.dateTime)}
              </Typography>
            </Stack>

            {event.location && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <LocationOnIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary" noWrap>
                  {event.location}
                </Typography>
              </Stack>
            )}

            {event.billingEstimate && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <PeopleIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {event.billingEstimate.guestCount} guest{event.billingEstimate.guestCount !== 1 ? 's' : ''}
                </Typography>
              </Stack>
            )}
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

function LoadingSkeleton() {
  return (
    <Grid container spacing={3}>
      {[0, 1, 2].map((i) => (
        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
          <Card>
            <CardContent>
              <Skeleton variant="text" width="60%" height={32} />
              <Skeleton variant="text" width="40%" sx={{ mt: 1 }} />
              <Skeleton variant="text" width="80%" />
              <Skeleton variant="text" width="30%" />
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}

function EmptyState() {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        textAlign: 'center',
        py: 8,
        px: 3,
      }}
    >
      <EventIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
      <Typography variant="h5" gutterBottom>
        No events yet
      </Typography>
      <Typography variant="body1" color="text.secondary" mb={3}>
        Create your first event and start inviting guests in under 5 minutes.
      </Typography>
      <Button
        variant="contained"
        size="large"
        startIcon={<AddIcon />}
        onClick={() => navigate('/events/new')}
      >
        Create Your First Event
      </Button>
    </Box>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { host } = useAuth();
  const [page, setPage] = useState(1);
  const limit = 12;
  const { data, isLoading, isError, error } = useEvents(page, limit);

  const firstName = host?.name?.split(' ')[0] ?? 'there';

  return (
    <Box>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        mb={4}
      >
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Welcome back, {firstName}
          </Typography>
          <Typography variant="body1" color="text.secondary" mt={0.5}>
            Manage your events and guest lists
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="large"
          startIcon={<AddIcon />}
          onClick={() => navigate('/events/new')}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Create New Event
        </Button>
      </Stack>

      {/* Error state */}
      {isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {(error as Error)?.message ?? 'Failed to load events. Please try again.'}
        </Alert>
      )}

      {/* Loading state */}
      {isLoading && <LoadingSkeleton />}

      {/* Empty state */}
      {!isLoading && !isError && data && data.data.length === 0 && <EmptyState />}

      {/* Event grid */}
      {!isLoading && data && data.data.length > 0 && (
        <>
          <Grid container spacing={3}>
            {data.data.map((event) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={event.id}>
                <EventCard event={event} />
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          {data.totalPages > 1 && (
            <Stack alignItems="center" mt={4}>
              <Pagination
                count={data.totalPages}
                page={page}
                onChange={(_e, value) => setPage(value)}
                color="primary"
                size="large"
              />
            </Stack>
          )}
        </>
      )}
    </Box>
  );
}
